import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@/data/store/utils/redux';
import { getUserInvitations } from '@/data/store/selectors';
import {
  acceptSelfInvitationAsync,
  getAllOrganizationsAsync,
  getSelfInvitationsAsync,
  rejectSelfInvitationAsync,
  storeOrganizationId,
} from '@/data/store/actions';
import { TUserSelfInvitationItem } from '@/data/types';
import useNotify from '@/hooks/useNotify';

/**
 * Shared in-flight fetch. An `OrgSwitcher` can be mounted more than once per
 * screen (the desktop sidebar and the mobile drawer each render one) and
 * StrictMode double-invokes effects on top of that. Without this guard the app
 * fires several identical GET /users/self/invitations on load. Every caller
 * reads the same redux slice, so one request serves all of them.
 */
let inFlight: Promise<unknown> | null = null;

/**
 * The organizations/projects that have invited the signed-in user, with
 * accept/decline.
 *
 * Shared by the org selection screen and the sidebar `OrgSwitcher`, which need
 * identical behaviour: GET /users/self/invitations is email-scoped, so it works
 * with or without an active organization. Accepting enters the accepted
 * organization immediately.
 *
 * `pendingOrgId` is the invitation currently in flight, so a caller can spin
 * just that row rather than the whole list.
 */
export const useOrgInvitations = () => {
  const dispatch = useAppDispatch();
  const notify = useNotify();
  const invitations = useSelector(getUserInvitations);

  const [pendingOrgId, setPendingOrgId] = useState<string | null>(null);

  const refresh = useCallback(() => {
    if (inFlight) return inFlight;
    inFlight = dispatch(getSelfInvitationsAsync({})).finally(() => {
      inFlight = null;
    });
    return inFlight;
  }, [dispatch]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const acceptInvitation = useCallback(
    async (inv: TUserSelfInvitationItem) => {
      setPendingOrgId(inv.id);
      try {
        await dispatch(acceptSelfInvitationAsync(inv.id)).unwrap();
        // The accepted organization now belongs to the user — reload both lists,
        // then drop the user straight into it.
        await Promise.all([dispatch(getAllOrganizationsAsync({})), dispatch(getSelfInvitationsAsync({}))]);
        dispatch(storeOrganizationId(inv.id));
        notify.success('Η πρόσκληση έγινε αποδεκτή!');
      } catch (error) {
        console.error('Failed to accept invitation:', error);
        notify.error('Σφάλμα κατά την αποδοχή της πρόσκλησης');
      } finally {
        setPendingOrgId(null);
      }
    },
    [dispatch, notify],
  );

  const declineInvitation = useCallback(
    async (inv: TUserSelfInvitationItem) => {
      setPendingOrgId(inv.id);
      try {
        await dispatch(rejectSelfInvitationAsync(inv.id)).unwrap();
        await dispatch(getSelfInvitationsAsync({}));
        notify.success('Η πρόσκληση απορρίφθηκε');
      } catch (error) {
        console.error('Failed to reject invitation:', error);
        notify.error('Σφάλμα κατά την απόρριψη της πρόσκλησης');
      } finally {
        setPendingOrgId(null);
      }
    },
    [dispatch, notify],
  );

  return {
    invitations,
    count: invitations.length,
    pendingOrgId,
    refresh,
    acceptInvitation,
    declineInvitation,
  };
};
